// Rahmen auf dem Telefon (design-system §3.9): Kopfzeile mit Standort, Glocke und Benutzer; untere Leiste mit fünf
// Plätzen, in der Mitte immer „Scannen“; „Mehr“ öffnet die vollständige Navigation als Sheet. Welches Modul die
// Plätze 2 und 4 bekommt, regelt planBottomBar (nav.ts); die Suche steht immer auch unter „Mehr“.
// Alle Ziele sind mindestens 48 px hoch.
import { ChevronDown, Inbox, Menu, ScanBarcode, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { cn } from '../lib/cn'
import { shell, useShellStore } from '../stores/shell'
import { Drawer } from '../ui/Drawer'
import { IconButton } from '../ui/IconButton'
import { useAssistantUnavailable } from './AssistantDock'
import { DemoMark } from './DemoMark'
import { activeNavItem, planBottomBar, START_ITEM, type NavItem, type Navigation } from './nav'
import { NotificationBell } from './NotificationCenter'
import { RouterLink } from './RouterLink'
import { NavTree } from './Sidebar'
import { LocationSwitcher, TenantSwitcher } from './Switchers'
import { AssistantMark } from './Topbar'
import { UserMenu } from './UserMenu'

export function MobileHeader() {
  const assistantOpen = useShellStore((state) => state.assistantOpen)
  const assistantOff = useAssistantUnavailable()
  return (
    <header
      className="flex shrink-0 items-center gap-1 border-b border-line bg-surface px-2 pt-safe"
      data-print="hide"
      data-testid="mobile-header"
    >
      <div className="flex h-14 min-w-0 flex-1 items-center gap-1">
        {/* der Standort nimmt den Platz, den sein Name braucht – gekürzt wird erst, wenn es wirklich eng ist */}
        <div className="flex min-w-0 flex-1">
          <LocationSwitcher className="w-auto max-w-full border-transparent bg-transparent shadow-none" />
        </div>
        <DemoMark compact />
        <NotificationBell />
        <IconButton
          icon={AssistantMark}
          label="KI-Assistent"
          pressed={assistantOpen}
          className={assistantOff ? 'text-fg-subtle' : undefined}
          onClick={shell.toggleAssistant}
          data-testid="assistant-toggle"
        />
        <UserMenu variant="avatar" side="bottom" />
      </div>
    </header>
  )
}

const slot =
  'flex h-14 min-w-0 flex-col items-center justify-center gap-0.5 text-micro tracking-normal normal-case transition-colors duration-(--duration-instant) focus-inset'

function BarLink({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <RouterLink
      href={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(slot, active ? 'font-medium text-fg' : 'text-fg-muted')}
    >
      <item.icon className="size-5" strokeWidth={active ? 2 : 1.75} aria-hidden />
      <span className="max-w-full truncate-line px-1">{item.label}</span>
    </RouterLink>
  )
}

export function BottomBar({ navigation }: { navigation: Navigation }) {
  const { pathname } = useLocation()
  const plan = planBottomBar(navigation)
  const second = plan.leading[1]
  const activeKey = activeNavItem(navigation.all, pathname)?.key ?? null
  const menuOpen = useShellStore((state) => state.mobileNavOpen)
  return (
    <nav
      aria-label="Schnellzugriff"
      data-testid="bottom-bar"
      data-ui="bottom-bar"
      data-print="hide"
      className="grid shrink-0 grid-cols-5 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)]"
    >
      <BarLink item={START_ITEM} active={activeKey === START_ITEM.key} />
      {second ? (
        <BarLink item={second} active={second.key === activeKey} />
      ) : (
        <button
          type="button"
          className={cn(slot, 'text-fg-muted')}
          onClick={() => shell.openNotifications('tasks')}
          data-testid="bottom-tasks"
        >
          <Inbox className="size-5" strokeWidth={1.75} aria-hidden />
          <span>Aufgaben</span>
        </button>
      )}
      <button
        type="button"
        aria-label="Scannen"
        data-testid="bottom-scan"
        className="flex h-14 items-center justify-center focus-inset"
        onClick={() => shell.setScan(true)}
      >
        <span className="flex size-11 items-center justify-center rounded-full bg-accent text-on-accent accent-edge shadow-sm">
          <ScanBarcode className="size-5" strokeWidth={1.75} aria-hidden />
        </span>
      </button>
      {plan.trailing ? (
        <BarLink item={plan.trailing} active={plan.trailing.key === activeKey} />
      ) : (
        <button
          type="button"
          className={cn(slot, 'text-fg-muted')}
          onClick={() => shell.openPalette()}
          data-testid="bottom-search"
        >
          <Search className="size-5" strokeWidth={1.75} aria-hidden />
          <span>Suche</span>
        </button>
      )}
      <button
        type="button"
        aria-expanded={menuOpen}
        data-testid="bottom-more"
        className={cn(slot, menuOpen ? 'font-medium text-fg' : 'text-fg-muted')}
        onClick={() => shell.setMobileNav(true)}
      >
        <Menu className="size-5" strokeWidth={1.75} aria-hidden />
        <span>Mehr</span>
      </button>
    </nav>
  )
}

/**
 * Hinweis am unteren Rand des Blatts, solange darunter noch Einträge liegen: Verlauf und ein Pfeil.
 * Das Blatt rollt ohne sichtbaren Balken – ohne den Hinweis endete die Liste scheinbar mitten in einer Gruppe.
 */
function MoreBelowHint() {
  const ref = useRef<HTMLDivElement | null>(null)
  const [below, setBelow] = useState(false)
  useEffect(() => {
    const node = ref.current?.closest<HTMLElement>('.overflow-y-auto') ?? null
    if (!node) return
    const measure = (): void => setBelow(node.scrollHeight - node.clientHeight - node.scrollTop > 8)
    measure()
    const later = window.setTimeout(measure, 350) // nach der Einblendung des Blatts
    node.addEventListener('scroll', measure, { passive: true })
    return () => {
      window.clearTimeout(later)
      node.removeEventListener('scroll', measure)
    }
  }, [])
  // rein optisch (pointer-events-none): Der Hinweis liegt über der Liste und darf keinen Tipp auf einen Eintrag abfangen
  return (
    <div
      ref={ref}
      data-testid="more-below"
      data-visible={below || undefined}
      aria-hidden
      className={cn(
        'pointer-events-none sticky -bottom-5 z-10 -mb-5 flex h-16 items-end justify-center bg-linear-to-t from-overlay via-overlay/85 to-transparent pb-2 text-fg-muted transition-opacity duration-(--duration-fast)',
        below ? 'opacity-100' : 'opacity-0',
      )}
    >
      <ChevronDown className="size-5" strokeWidth={1.75} />
    </div>
  )
}

/** Sheet „Mehr“: derselbe Baum wie in der Sidebar, dazu Betrieb, Suche und Einstellungen. */
export function MobileNavSheet({ navigation }: { navigation: Navigation }) {
  const open = useShellStore((state) => state.mobileNavOpen)
  const { pathname } = useLocation()
  const activeKey = activeNavItem(navigation.all, pathname)?.key ?? null
  const close = (): void => shell.setMobileNav(false)
  // beim Öffnen steht der Eintrag der aktuellen Seite im Bild – auch wenn seine Gruppe weit unten liegt
  const revealCurrent = (node: HTMLElement | null): void => {
    const current = node?.querySelector<HTMLElement>('[aria-current="page"]')
    if (current && typeof current.scrollIntoView === 'function') current.scrollIntoView({ block: 'center' })
  }
  return (
    <Drawer open={open} onOpenChange={shell.setMobileNav} title="Menü" side="bottom" data-testid="mobile-nav">
      <nav aria-label="Hauptnavigation" className="-mx-2 flex flex-col gap-0.5" ref={revealCurrent}>
        <TenantSwitcher />
        <button
          type="button"
          onClick={() => shell.openPalette()}
          data-testid="more-search"
          className="flex h-11 w-full items-center gap-2 rounded-sm px-2 text-touch text-fg-muted hover:bg-nav-hover"
        >
          <Search className="size-5 shrink-0" strokeWidth={1.75} aria-hidden />
          <span className="flex-1 text-left">Suchen …</span>
        </button>
        <NavTree navigation={navigation} onNavigate={close} />
        <div className="mt-2 flex flex-col gap-0.5 border-t border-line pt-2">
          <button
            type="button"
            onClick={() => shell.setAssistant(true)}
            className="flex h-11 w-full items-center gap-2 rounded-sm px-2 text-touch text-fg-muted hover:bg-nav-hover"
          >
            <span className="flex size-5 shrink-0 items-center justify-center">
              <AssistantMark />
            </span>
            <span className="flex-1 text-left">KI-Assistent</span>
          </button>
          {navigation.settings.map((item) => (
            <RouterLink
              key={item.key}
              href={item.to}
              onClick={close}
              aria-current={item.key === activeKey ? 'page' : undefined}
              className="flex h-11 items-center gap-2 rounded-sm px-2 text-touch text-fg-muted hover:bg-nav-hover"
            >
              <item.icon className="size-5 shrink-0" strokeWidth={1.75} aria-hidden />
              {item.label}
            </RouterLink>
          ))}
        </div>
        <MoreBelowHint />
      </nav>
    </Drawer>
  )
}
